import {
  Building2,
  Clock,
  History,
  MessageCircleMore,
  NotebookPen,
  Stethoscope,
  TriangleAlert,
} from 'lucide-react'
import { type ReactNode, useId, useState } from 'react'
import { toast } from 'sonner'

import type { AppointmentStatus, AppointmentWithRelations, DateFormat } from '@snootsvet/shared'
import { formatName, resolveDateFormat, wallClockDayDiff } from '@snootsvet/shared'

import { ExpandBadge } from '@/frontend/components/expand-badge'
import {
  FormattedDateTime,
  recordFrame,
  useViewerFormat,
} from '@/frontend/components/formatted-datetime'
import { Alert, AlertDescription } from '@/frontend/components/ui/alert'
import { Button } from '@/frontend/components/ui/button'
import { Checkbox } from '@/frontend/components/ui/checkbox'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/frontend/components/ui/dialog'
import { Label } from '@/frontend/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/frontend/components/ui/select'
import { UpcomingBadge } from '@/frontend/components/upcoming-badge'
import {
  AppointmentActionsMenu,
  NoShowAppointmentAction,
} from '@/frontend/features/appointments/appointment-actions'
import { AppointmentActivityLog } from '@/frontend/features/appointments/appointment-activity-log'
import { AppointmentStatusBadge } from '@/frontend/features/appointments/appointment-status-badge'
import {
  getCategoryStyle,
  getDisplayStatus,
} from '@/frontend/features/appointments/appointments.utils'
import { useActiveConsultation } from '@/frontend/features/consultations/active-consultation-context'
import { useViewerRegion } from '@/frontend/features/region/region-context'
import { useListPatientAppointments } from '@/frontend/hooks/api/patients/use-list-patient-appointments'
import { useCreateConsultation } from '@/frontend/hooks/api/use-create-consultation'
import { useGetAuthenticatedStaff } from '@/frontend/hooks/api/use-get-authenticated-staff'
import { useListStaff } from '@/frontend/hooks/api/use-list-staff'
import { usePatchAppointment } from '@/frontend/hooks/api/use-patch-appointment'

/** Appointment statuses that are still "live" — not cancelled, missed, or done. */
const ACTIVE_APPOINTMENT_STATUSES = new Set<AppointmentStatus>([
  'pending',
  'confirmed',
  'checked-in',
  'in-progress',
])

/** A warning to show before creating a consultation, with a key for the list. */
type ConsultationWarning = { key: string; content: ReactNode }

/**
 * Computes warnings to surface before creating a consultation for an
 * appointment. Warns when the appointment isn't the patient's next upcoming
 * one, isn't scheduled for today.
 *
 * Returns nodes rather than strings so the dates can render through the shared
 * timestamp components, like every other date on screen.
 *
 * Resolves days in the appointment's own frame — its clinic's zone, the viewer's
 * when it has none — which is the frame the card's `isFuture` flag comes from,
 * so this dialog can't warn about a different day than the card that opened it
 * thinks it's on.
 *
 * `now` is injectable for testing.
 */
function getConsultationWarnings(
  appointment: AppointmentWithRelations,
  patientAppointments: AppointmentWithRelations[],
  viewerFmt: DateFormat,
  now: Date = new Date()
): ConsultationWarning[] {
  const warnings: ConsultationWarning[] = []
  const { timeZone } = recordFrame(appointment.clinicId, viewerFmt)
  const start = new Date(appointment.startTime)

  // (1) Not the next appointment — another active appointment starts sooner.
  const soonerUpcoming = patientAppointments
    .filter(
      (a) =>
        a.id !== appointment.id &&
        ACTIVE_APPOINTMENT_STATUSES.has(a.status) &&
        new Date(a.startTime) >= now &&
        new Date(a.startTime) < start
    )
    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())[0]
  if (soonerUpcoming) {
    const soonerType = soonerUpcoming.appointmentTypeName
      ? `${soonerUpcoming.appointmentTypeName} appointment`
      : 'appointment'
    warnings.push({
      key: 'earlier-appointment',
      content: (
        <>
          {/* Read at the *sooner* appointment's own clinic — it may differ. */}
          This patient has an earlier {soonerType} (
          <FormattedDateTime
            value={soonerUpcoming.startTime}
            clinicId={soonerUpcoming.clinic?.id ?? null}
          />
          ). Are you sure you want to create a consultation for this one instead?
        </>
      ),
    })
  }

  // (2) Not today — the appointment is scheduled for a different calendar day.
  // Same day-granular rule as the card's "Upcoming" treatment: both resolve in
  // the appointment's own frame — its clinic's zone, the viewer's when it has
  // none — so the two agree.
  const diffDays = wallClockDayDiff(start, timeZone, now)
  if (diffDays !== 0) {
    const dayLabel = Math.abs(diffDays) === 1 ? 'day' : 'days'
    const timing =
      diffDays > 0 ? `is in ${diffDays} ${dayLabel}` : `was ${Math.abs(diffDays)} ${dayLabel} ago`
    warnings.push({
      key: 'not-today',
      content: `This appointment ${timing}. Are you sure you want to create a consultation for it?`,
    })
  }

  return warnings
}

interface AppointmentEntryProps {
  appointment: AppointmentWithRelations
  /** Dated on a later calendar day — styled as provisional, primary actions demoted. */
  isFuture?: boolean
}

/** Timeline entry for an appointment without an active consultation. */
export function AppointmentEntry({ appointment, isFuture = false }: AppointmentEntryProps) {
  const [createConsultOpen, setCreateConsultOpen] = useState(false)
  const { region: viewerRegion } = useViewerRegion()

  const { timeZone } = resolveDateFormat(appointment.clinicId, viewerRegion)
  const displayStatus = getDisplayStatus(appointment)
  const canCreateConsultation = appointment.status !== 'cancelled'
  const activityLog = appointment.activityLog ?? []
  const [historyOpen, setHistoryOpen] = useState(false)

  return (
    <>
      <div
        className="overflow-hidden rounded-xl bg-card ring-1 ring-foreground/10 border-t-2"
        style={{
          borderTopColor: getCategoryStyle(appointment.appointmentTypeId).border,
          backgroundColor: isFuture
            ? undefined
            : `color-mix(in oklab, ${getCategoryStyle(appointment.appointmentTypeId).bg} 15%, white)`,
        }}
      >
        <div className="px-4 py-3">
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2.5">
                <span className="flex items-center gap-1">
                  <span className="text-headline-sm">
                    {appointment.appointmentTypeName ?? 'Appointment'}
                  </span>
                  {(() => {
                    const style = getCategoryStyle(appointment.appointmentTypeId)
                    return (
                      <style.icon
                        className="size-3"
                        style={style.text ? { color: style.text } : undefined}
                      />
                    )
                  })()}
                </span>
                <AppointmentStatusBadge status={displayStatus} size="xs" />
                {isFuture && <UpcomingBadge />}
                {activityLog.length > 0 && (
                  <ExpandBadge
                    open={historyOpen}
                    onToggle={() => setHistoryOpen(!historyOpen)}
                    icon={<History className="size-3" />}
                  >
                    History ({activityLog.length})
                  </ExpandBadge>
                )}
              </div>
              {/* Intentionally a subset of APPOINTMENT_STATUS_TRANSITIONS — the timeline
                only surfaces happy-path actions. Unhappy-path corrections (e.g. reversing
                a no-show) go through the calendar detail view.

                On a future appointment both step aside: it can't have been missed yet,
                and starting its consultation early is the mistake this card exists to
                prevent, so that action moves into the menu where it takes a deliberate
                second click. */}
              <div className="flex items-center gap-1.5 -my-1">
                {!isFuture && ['pending', 'confirmed'].includes(appointment.status) && (
                  <NoShowAppointmentAction appointment={appointment} />
                )}
                {!isFuture && canCreateConsultation && (
                  <Button onClick={() => setCreateConsultOpen(true)}>Create Consultation</Button>
                )}
                <AppointmentActionsMenu
                  appointment={appointment}
                  timeZone={timeZone}
                  onCreateConsultation={
                    isFuture && canCreateConsultation ? () => setCreateConsultOpen(true) : undefined
                  }
                />
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="size-3 shrink-0" />
                <FormattedDateTime
                  value={appointment.startTime}
                  clinicId={appointment.clinic?.id ?? null}
                />
              </span>
              <span className="flex items-center gap-1">
                <Stethoscope className="size-3 shrink-0" />
                {appointment.staff.firstName} {appointment.staff.lastName}
              </span>
              {appointment.clinic && (
                <span className="flex items-center gap-1">
                  <Building2 className="size-3 shrink-0" />
                  {appointment.clinic.name}
                </span>
              )}
            </div>
            {appointment.visitReason && (
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <NotebookPen className="size-3 shrink-0" />
                <p>{appointment.visitReason}</p>
              </div>
            )}
            {appointment.userNotes && (
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <MessageCircleMore className="size-3 shrink-0" />
                <p>{appointment.userNotes}</p>
              </div>
            )}
          </div>
        </div>
        {historyOpen && (
          <div className="border-t px-4 py-2">
            <AppointmentActivityLog activityLog={activityLog} clinicId={appointment.clinicId} />
          </div>
        )}
      </div>

      <CreateAppointmentConsultationDialog
        appointment={appointment}
        open={createConsultOpen}
        onOpenChange={setCreateConsultOpen}
      />
    </>
  )
}

/** Dialog for creating a consultation linked to an appointment. */
function CreateAppointmentConsultationDialog({
  appointment,
  open,
  onOpenChange,
}: {
  appointment: AppointmentWithRelations
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { consultationId, open: openConsultation } = useActiveConsultation()
  const { mutateAsync: createConsultation, isPending: isCreating } = useCreateConsultation()
  const { mutate: patchAppointment } = usePatchAppointment()
  const { data: allStaff = [] } = useListStaff()
  const { data: patientAppointments = [] } = useListPatientAppointments(appointment.patientId)
  const useMeId = useId()
  const checkInId = useId()

  const { data: authenticatedStaff } = useGetAuthenticatedStaff()

  const [staffId, setStaffId] = useState(appointment.staffId)
  const [useMe, setUseMe] = useState(false)
  const [checkInAppointment, setCheckInAppointment] = useState(true)
  const alreadyCheckedIn = appointment.status === 'checked-in'

  // Surface warnings when creating a consultation for anything but the patient's next, same-day appointment.
  const warnings = getConsultationWarnings(appointment, patientAppointments, useViewerFormat())

  function handleToggleMe(checked: boolean) {
    setUseMe(checked)
    if (checked && authenticatedStaff) {
      setStaffId(authenticatedStaff.id)
    } else {
      setStaffId(appointment.staffId)
    }
  }

  const staffItems = allStaff.map((s) => ({
    label: formatName(s) || s.email,
    value: s.id,
  }))

  async function handleConfirm() {
    if (consultationId) {
      toast.warning('Please close the current consultation first')
      return
    }
    try {
      const { consultation } = await createConsultation({
        patientId: appointment.patientId,
        appointmentId: appointment.id,
        clinicId: appointment.clinicId ?? undefined,
        staffId,
      })
      if (checkInAppointment && !alreadyCheckedIn) {
        patchAppointment({ id: appointment.id, status: 'checked-in' })
      }
      openConsultation(consultation.id)
      onOpenChange(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create consultation')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create Consultation</DialogTitle>
        </DialogHeader>
        {warnings.length > 0 && (
          <Alert variant="warning">
            <TriangleAlert className="size-4" />
            <AlertDescription>
              <ul className="flex list-disc flex-col gap-1 pl-4">
                {warnings.map((warning) => (
                  <li key={warning.key}>{warning.content}</li>
                ))}
              </ul>
              <p className="mt-2 text-muted-foreground">
                If this consultation isn't related to an appointment, you can create a standalone
                one from the extra actions menu on the patient's page instead.
              </p>
            </AlertDescription>
          </Alert>
        )}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label>Staff member</Label>
            <div className="flex items-center gap-2">
              <Select
                value={staffId}
                onValueChange={(v) => setStaffId(v ?? appointment.staffId)}
                items={staffItems}
                disabled={useMe}
              >
                <SelectTrigger className="w-full flex-1">
                  <SelectValue placeholder="Select staff member" />
                </SelectTrigger>
                <SelectContent>
                  {staffItems.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex shrink-0 items-center gap-1.5">
                <Checkbox
                  id={useMeId}
                  checked={useMe}
                  onCheckedChange={(checked) => handleToggleMe(checked === true)}
                />
                <label htmlFor={useMeId} className="cursor-pointer">
                  Me
                </label>
              </div>
            </div>
          </div>
          {!alreadyCheckedIn && (
            <div className="flex items-center gap-2">
              <Checkbox
                id={checkInId}
                checked={checkInAppointment}
                onCheckedChange={(checked) => setCheckInAppointment(checked === true)}
              />
              <label htmlFor={checkInId} className="font-medium cursor-pointer">
                Mark appointment as checked in
              </label>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isCreating}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isCreating || !staffId}>
            {isCreating ? 'Creating…' : 'Create'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
